import { Progress } from '@agro-club/agroclub-shared'
import { useAFormHandler } from 'analytics/hooks'
import { useFormik } from 'formik'
import { Deal } from 'modules/domain/deal/types'
import { endpoints } from 'modules/endpoints'
import { apiClient } from 'modules/utils/httpClient'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { AButton } from 'views/components/Analytics'
import ADefaultModalFooter from 'views/components/DefaultModalFooter/ADefaultModalFooter'
import { ResponsiveModal } from 'views/components/ResponsiveModal/ResponsiveModal'
import { SharedModalContent, SharedModalInputs } from 'views/components/SharedModal'
import { AnalysisTypes, LaboratoryCodes } from 'views/pages/Samples/types'
import { CommonFormFields } from './CommonFormFields'
import { Bid } from 'modules/domain/bid/types'

const ButtonsWrapper = styled.div`
  display: flex;
  align-items: center;
  column-gap: 16px;
  margin-top: 24px;
`

type Props = {
  bid: Bid
  deal?: Deal
  onClose: () => void
  changeDealStatusHandler?: () => void
  changeDealStatusProgress?: Progress
  refetch: () => void
}

export type AddSampleRequestFields = {
  bid: string
  deal: string | null
  senior_regional: string | null
  quantity: number
  product: string
  seller_user: string
  seller_address: string
  phone: string
  laboratory: LaboratoryCodes | null
  lab_comment: string | null
  lab_assistant: string | null
  analysis_type: AnalysisTypes | null
  comment: string | null
}

export const AddSampleRequestModal: FC<Props> = ({
  bid,
  deal,
  onClose,
  changeDealStatusHandler,
  refetch,
  changeDealStatusProgress,
}) => {
  const { t } = useTranslation(['deal', 'common'])
  const { formProgress, formHandler } = useAFormHandler()

  const handleChangeDealStatus = async () => {
    await changeDealStatusHandler?.()
    onClose()
  }

  const formik = useFormik<AddSampleRequestFields>({
    initialValues: {
      bid: bid.id,
      deal: deal ? deal.id : null,
      senior_regional: null,
      quantity: deal ? deal.quantity : bid.quantity,
      product: bid.product.id,
      seller_user: bid.owner.id,
      seller_address: bid.address.id,
      phone: bid.owner.phone,
      laboratory: LaboratoryCodes.voronezh,
      lab_comment: null,
      lab_assistant: null,
      analysis_type: AnalysisTypes.gost,
      comment: null,
    },

    onSubmit: formHandler(() => apiClient.post(endpoints.sampleRequest(), formik.values), {
      onSuccess: async () => {
        if (changeDealStatusHandler) {
          await changeDealStatusHandler()
        } else {
          await refetch()
        }

        onClose()
      },
      onError: (error) => {
        const { errors } = error
        formik.setErrors(errors)
      },
    }),
  })

  const customFooterJSX = (
    <>
      {changeDealStatusHandler ? (
        <ButtonsWrapper>
          <AButton
            id="sendSampleRequestAndChangeDealStatus"
            size="big"
            progress={formProgress}
            onClick={formik.submitForm}
          >
            {t('accordion.sample.sendBib')}
          </AButton>
          <AButton
            id="changeDealStatus"
            size="big"
            progress={changeDealStatusProgress}
            color="secondary"
            onClick={handleChangeDealStatus}
          >
            {t('accordion.sample.createLater')}
          </AButton>
        </ButtonsWrapper>
      ) : (
        <ADefaultModalFooter
          onClose={onClose}
          confirmButtonText={t('common:send')}
          progress={formProgress}
          onConfirm={formik.submitForm}
        />
      )}
    </>
  )

  return (
    <ResponsiveModal
      id="addSampleRequest"
      title={t('accordion.sample.sampleRequest')}
      progress={formProgress}
      onConfirm={formik.submitForm}
      onClose={onClose}
      size="large"
      customFooter={customFooterJSX}
    >
      <SharedModalContent>
        <SharedModalInputs>
          <CommonFormFields formik={formik} />
        </SharedModalInputs>
      </SharedModalContent>
    </ResponsiveModal>
  )
}
